import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import Overlay from '../Overlay';
import InlineMessage from '../InlineMessage';
import useMessage from '../../lib/useMessage';
// The blocker vocabulary comes from the builder rather than being retyped here.
// A duplicated code string is a second copy of a constant, and it drifts in
// exactly the half nobody is editing.
import { BLOCK } from '../../lib/quickbooksBill';

// Send to QuickBooks — Issue #298, gate screen 4, Session 78n9.3.
//
// WHAT THIS IS, in plain English: Bridgette has typed a vendor's invoice into
// Stede. This shows her exactly what QuickBooks is about to be told, and then
// tells it. She never retypes the invoice and there is no file to convert.
//
// THE SHEET DRAWS ITSELF FROM A PREVIEW, and that is the design rather than a
// convenience. /quickbooks-bill has two modes: preview builds the payload and
// sends nothing; send builds it again, server-side, from the same rows. So the
// numbers on this screen are not a summary of what will be sent — they ARE the
// object that gets sent, read back before anyone commits to it.
//
// Nothing here computes money. The browser is not trusted to decide what a
// bill comes to, because anyone who can open a console can change what the
// browser decides. Every figure below arrives from the server.
//
// Gate rule G: block the button and SAY WHY, rather than failing the click.

const MAGENTA = '#B31F7C';
const INK = '#0F0F0F';
const MUTED = '#5C5C5C';
const FAINT = '#9A9A9A';
const RULE = '#E8E8E8';
const BAD = '#A32D2D';
const WARN = '#8A5A00';

function money(n) {
  const v = Number(n || 0);
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function callBill(body) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return { error: 'Your session has expired — please sign in again.' };

  let res;
  try {
    res = await fetch('/quickbooks-bill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { error: 'Could not reach QuickBooks. ' + (e.message || '') };
  }

  // #377's shape: under `npm start` an unknown path returns index.html, so
  // res.json() throws on a page of HTML and the real cause reads as the app
  // being broken.
  const ct = res.headers.get('Content-Type') || '';
  if (!ct.includes('application/json')) {
    return { error: 'Sending to QuickBooks needs the deployed site — this service is not served by the local dev server.' };
  }
  const out = await res.json();
  return { ...out, httpStatus: res.status };
}

export default function SendToQuickBooksSheet({ invoice, isMobile, onClose, onSent }) {
  const msg = useMessage();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);
  // #430, Session 78n9.4. Bridgette accepting an unresolved discrepancy.
  //
  // It re-previews rather than merely unlocking the button, because the server
  // is the thing that decides. Ticking asks it again WITH the acceptance, and
  // what comes back is the real payload built under that acceptance -- so what
  // she approves is still the object that gets posted, which is the property
  // the whole preview mode exists to hold.
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(async (acceptVariance) => {
    setLoading(true);
    const out = await callBill({ invoiceId: invoice.id, preview: true, acceptVariance: !!acceptVariance });
    setPreview(out);
    setLoading(false);
  }, [invoice.id]);

  useEffect(() => { load(false); }, [load]);

  function toggleAccept() {
    const next = !accepted;
    setAccepted(next);
    load(next);
  }

  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];
  const lines = preview?.payload?.Line || [];
  const isCredit = preview?.kind === 'VendorCredit';
  const noun = isCredit ? 'vendor credit' : 'bill';
  const canSend = !loading && !preview?.error && blockers.length === 0 && !!preview?.payload && !result;
  // #430. The variance blocker standing right now, which is what turns Cancel
  // into a way forward rather than a dead end.
  const varianceBlocked = blockers.some(b => b.code === BLOCK.UNRESOLVED_DISCREPANCY);

  async function send() {
    setWorking(true);
    msg.clearMessage();
    const out = await callBill({ invoiceId: invoice.id, acceptVariance: accepted });
    setWorking(false);

    // The dangerous case, and it is checked FIRST because it is the one where
    // the obvious reaction is wrong. The bill exists in QuickBooks and Stede
    // failed to write it down; sending again would duplicate a real liability.
    // `outcome`, not `kind`. The server's own reply carries kind: 'Bill' |
    // 'VendorCredit', so an object built as { kind: 'ok', ...out } has its
    // discriminator overwritten by the spread and every branch below silently
    // stops matching. That shipped, and the live test caught it: the sheet
    // stayed open with a Done button and no message at all, because `result`
    // was truthy while neither comparison was true.
    if (out.sent && !out.recorded) {
      setResult({ ...out, outcome: 'half' });
      return;
    }
    if (out.error) { msg.showError(out.error + (out.detail ? ' — ' + out.detail : '')); return; }
    if (!out.sent) {
      // Blocked between opening this sheet and pressing the button — somebody
      // else sent it. Re-render the blocker rather than claiming a failure.
      setPreview(out);
      msg.showWarning('This invoice was not sent. See the reason below.');
      return;
    }
    setResult({ ...out, outcome: 'ok' });
    if (onSent) onSent(out);
  }

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={working ? undefined : onClose} maxWidth={560}>
      <div className="modal-header">
        <span className="modal-title">Send to QuickBooks</span>
        <button className="close-btn" onClick={onClose} disabled={working}>×</button>
      </div>

      <div style={{ padding: '14px 16px 18px' }}>
        {msg.message && (
          <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} style={{ marginBottom: 12 }} />
        )}

        {loading && <p style={{ fontSize: 14, color: FAINT, margin: 0 }}>Checking this invoice…</p>}

        {!loading && preview?.error && (
          <InlineMessage type="error" message={preview.error} />
        )}

        {/* ------------------------------------------------------------------
            The half-sent case. Deliberately not styled as a success and not
            styled as a failure, because it is neither, and the instruction
            matters more than the tone: do not send it again.
           ------------------------------------------------------------------ */}
        {result?.outcome === 'half' && (
          <div style={{ border: `1px solid #E4A9A9`, background: '#FFF5F6', borderRadius: 8, padding: 14 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: BAD, margin: '0 0 8px' }}>
              The {noun} WAS created in QuickBooks, but Stede could not record it.
            </p>
            <p style={{ fontSize: 13, color: INK, margin: '0 0 8px', lineHeight: 1.5 }}>
              <b>Do not send it again</b> — that would create a second {noun} for the same invoice.
              Ask an administrator to reconcile it by hand.
            </p>
            <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>
              QuickBooks reference <b>{result.quickbooksId || 'unknown'}</b>
              {invoice.invoice_ref ? <> · invoice <b>{invoice.invoice_ref}</b></> : null}
            </p>
          </div>
        )}

        {result?.outcome === 'ok' && (
          <div style={{ border: `1px solid ${MAGENTA}`, background: '#FDF2F9', borderRadius: 8, padding: 14 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: MAGENTA, margin: '0 0 6px' }}>
              Sent to QuickBooks.
            </p>
            <p style={{ fontSize: 13, color: INK, margin: 0, lineHeight: 1.5 }}>
              {isCredit ? 'Vendor credit' : 'Bill'} <b>{result.docNumber || invoice.invoice_ref || result.quickbooksId}</b> now
              exists in QuickBooks. Stede will not offer to send this invoice again.
            </p>
            {result.auditLogged === false && (
              <p style={{ fontSize: 12, color: WARN, margin: '8px 0 0' }}>
                The export audit row could not be written. The {noun} itself is fine.
              </p>
            )}
          </div>
        )}

        {/* The preview. Hidden once a terminal result is showing — leaving a
            "here is what will be sent" panel under "it was sent" invites a
            second press. */}
        {!loading && !preview?.error && !result && (
          <>
            {blockers.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {blockers.map(b => (
                  <div key={b.code} style={{
                    border: '1px solid #E4A9A9', background: '#FFF5F6', borderRadius: 8,
                    padding: '10px 12px', marginBottom: 8,
                  }}>
                    <p style={{ fontSize: 13, color: BAD, margin: 0, lineHeight: 1.5 }}>{b.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* #430. The variance box is drawn from `variance.flagged` rather
                than from the blocker, and that is the fix for a defect this
                sheet shipped with for about ten minutes: when the tick lived
                inside the blocker, ticking it cleared the blocker, which
                removed the box, which removed the tick. Bridgette could accept
                a $150 variance and then had no way to take it back short of
                closing the sheet.

                An acceptance has to be as reversible as it was deliberate, so
                it stays on screen in both states, changes colour, and says
                which one it is in. */}
            {preview?.variance?.flagged && (
              <div style={{
                border: '1px solid ' + (accepted ? '#F3D9A8' : '#E4A9A9'),
                background: accepted ? '#FFF8EC' : '#FFF5F6',
                borderRadius: 8, padding: '10px 12px', marginBottom: 14,
              }}>
                <p style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                  color: accepted ? '#8A5A08' : BAD, margin: 0,
                }}>
                  {accepted ? 'Sending with an accepted variance' : 'This invoice never matched its order'}
                </p>
                {accepted && (
                  <p style={{ fontSize: 12.5, color: INK, margin: '6px 0 0', lineHeight: 1.5 }}>
                    {preview?.variance?.amount
                      ? money(preview.variance.amount) + ' of this bill is unexplained. '
                      : 'Part of this bill is unexplained. '}
                    It will be recorded against your name in the export history.
                  </p>
                )}
                <label style={{
                  display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer',
                  marginTop: 10, paddingTop: 9,
                  borderTop: '1px solid ' + (accepted ? '#F3D9A8' : '#E4A9A9'),
                }}>
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={toggleAccept}
                    disabled={loading || working}
                    style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0, accentColor: BAD }}
                  />
                  <span style={{ fontSize: 13, color: INK, lineHeight: 1.5 }}>
                    Send it anyway.{preview?.variance?.amount
                      ? ' I accept the ' + money(preview.variance.amount) + ' variance.'
                      : ' I accept the variance.'}
                  </span>
                </label>
              </div>
            )}

            {blockers.length === 0 && (
              <>
                <p style={{ fontSize: 13, color: MUTED, margin: '0 0 14px', lineHeight: 1.5 }}>
                  This is exactly what QuickBooks will be told. Nothing has been sent yet.
                </p>

                <Row label="Supplier" value={invoice.__vendorName || preview?.payload?.VendorRef?.value} />
                <Row label={isCredit ? 'Credit number' : 'Bill number'} value={preview?.payload?.DocNumber || '— none —'} />
                <Row label="Bill date" value={preview?.payload?.TxnDate} />
                {preview?.payload?.DueDate && <Row label="Due date" value={preview.payload.DueDate} />}

                <p style={{
                  fontSize: 10.5, fontWeight: 700, color: FAINT, textTransform: 'uppercase',
                  letterSpacing: 0.5, margin: '16px 0 8px',
                }}>
                  {lines.length} line{lines.length === 1 ? '' : 's'}
                </p>

                {lines.map((l, i) => (
                  <div key={i} style={{ padding: '9px 0', borderBottom: `1px solid ${RULE}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ fontSize: 13, color: INK }}>{l.Description}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {money(l.Amount)}
                      </span>
                    </div>
                    {/* D18: the account is DETERMINED, never chosen here. An
                        override at send time makes the books depend on who was
                        clicking, and it is invisible afterwards. Shown so it can
                        be checked; not editable, so it cannot be improvised. */}
                    <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
                      {l.AccountBasedExpenseLineDetail?.AccountRef?.name || 'Account ' + l.AccountBasedExpenseLineDetail?.AccountRef?.value}
                    </div>
                  </div>
                ))}

                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  padding: '12px 0 0', marginTop: 4,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>Total</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}>
                    {money(preview?.totals?.total)}
                  </span>
                </div>

                {preview?.payload?.PrivateNote && (
                  <p style={{ fontSize: 11.5, color: FAINT, margin: '10px 0 0', lineHeight: 1.5 }}>
                    Recorded on the {noun} in QuickBooks: “{preview.payload.PrivateNote}”
                  </p>
                )}

                {warnings.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    {warnings.map((w, i) => (
                      <p key={i} style={{ fontSize: 12.5, color: WARN, margin: '0 0 6px', lineHeight: 1.45 }}>{w}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          {/* #430. On the variance blocker this reads "Resolve it first" rather
              than "Cancel". Both close the sheet, but one names the way forward
              and the other names giving up -- and the way forward is on the
              Discrepancies tab, one tab away. */}
          <button className="btn btn-secondary" onClick={onClose} disabled={working}>
            {result ? 'Done' : (varianceBlocked ? 'Resolve it first' : 'Cancel')}
          </button>
          {!result && (
            <button
              className="btn btn-primary"
              onClick={send}
              disabled={!canSend || working}
              title={blockers.length > 0 ? 'This invoice cannot be sent yet — see above.' : undefined}
            >
              {working ? 'Sending…' : `Send ${noun}`}
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0' }}>
      <span style={{ fontSize: 12.5, color: MUTED }}>{label}</span>
      <span style={{ fontSize: 12.5, color: INK, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
}
